export interface SuperAdminOrganizationDetail {
  id: number;
  name: string;
  abbreviation: string;
  isSandbox: boolean;
  atRiskWindowDays: number;
  taskDelayDurationDays: number;
  allocatedPto: number;
  activeUserCount: number;
  roleCount: number;
  workflowDefinitionCount: number;
  workflowAssignmentCount: number;
  onboardingStatus: string;
  readiness: {
    isReady: boolean;
    findings: string[];
  };
}

export interface SuperAdminOrganizationEmployee {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  employeeId?: string;
  phoneNumber?: string;
  preferredTimeZone: string;
  hireDate?: string;
  birthday?: string;
  isActive: boolean;
  deactivated: boolean;
  roles: { id: number; name: string; protected: boolean }[];
  regions: { id: number; name: string }[];
  waiverFamilies: { id: number; name: string }[];
  teams: { id: number; name: string }[];
}

export interface CreateSuperAdminOrganizationRequest {
  name: string;
  abbreviation: string;
  ownerEmail?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
}

export interface InviteOrganizationEmployeeRequest {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  roleId: number;
  regionIds: number[];
  waiverFamilyIds: number[];
  teamIds: number[];
}

export interface SuperAdminOrganizationConfiguration {
  roles: { id: number; name: string; protected: boolean }[];
  regions: { id: number; name: string }[];
  waiverFamilies: { id: number; name: string }[];
  teams: { id: number; name: string }[];
}

export interface OrganizationInvitation {
  id: string;
  organizationId: number;
  purpose: "organization_owner" | "employee";
  email: string;
  status: "active" | "accepted" | "revoked" | "expired";
  createdAt: string;
  expiresAt: string;
  revokedAt?: string;
  usedAt?: string;
}

const organizationUrl = (organizationId?: number) =>
  `${process.env.REACT_APP_API_BASE_URL}/superadmin/organizations${organizationId ? `/${organizationId}` : ""}`;

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body.message || body.Message || body.code || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

async function request<T>(url: string, authToken: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${authToken}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

function mapOverview(detail: any): SuperAdminOrganizationDetail {
  return {
    ...detail,
    activeUserCount: detail.activeEmployeeCount,
    workflowDefinitionCount: detail.workflowCount,
    workflowAssignmentCount: detail.activeWorkflowAssignmentCount,
    onboardingStatus: detail.lifecycle,
    isSandbox: Boolean(detail.isSandbox),
    readiness: {
      isReady: detail.lifecycle === "READY" || detail.lifecycle === "ACTIVE",
      findings: (detail.onboardingSections || [])
        .filter((section: any) => !section.complete)
        .map((section: any) => section.detail || `${section.label} is incomplete.`),
    },
  };
}

export function getOrganizationWorkspace(
  authToken: string,
  organizationId: number,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationDetail> {
  return request<any>(organizationUrl(organizationId), authToken, { signal }).then(mapOverview);
}

export function createOrganization(
  authToken: string,
  payload: CreateSuperAdminOrganizationRequest,
): Promise<SuperAdminOrganizationDetail> {
  return request<any>(organizationUrl(), authToken, { method: "POST", body: JSON.stringify(payload) }).then(mapOverview);
}

export function updateOrganization(
  authToken: string,
  organizationId: number,
  payload: Pick<SuperAdminOrganizationDetail, "name" | "abbreviation" | "atRiskWindowDays" | "taskDelayDurationDays" | "allocatedPto">,
): Promise<SuperAdminOrganizationDetail> {
  return request<any>(organizationUrl(organizationId), authToken, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then(mapOverview);
}

export function getOrganizationEmployees(
  authToken: string,
  organizationId: number,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationEmployee[]> {
  return request(`${organizationUrl(organizationId)}/employees`, authToken, { signal });
}

export function getOrganizationConfiguration(
  authToken: string,
  organizationId: number,
  signal?: AbortSignal,
): Promise<SuperAdminOrganizationConfiguration> {
  return request(`${organizationUrl(organizationId)}/configuration`, authToken, { signal });
}

export function getOwnerInvitations(
  authToken: string,
  organizationId: number,
  signal?: AbortSignal,
): Promise<OrganizationInvitation[]> {
  return request(`${organizationUrl(organizationId)}/owner-invitations`, authToken, { signal });
}

export function issueOwnerInvitation(
  authToken: string,
  organizationId: number,
  email: string,
): Promise<OrganizationInvitation> {
  return request(`${organizationUrl(organizationId)}/owner-invitations`, authToken, {
    method: "POST",
    body: JSON.stringify({ email, expiresInHours: 72 }),
  });
}

export function resendOwnerInvitation(
  authToken: string,
  organizationId: number,
  invitationId: string,
): Promise<OrganizationInvitation> {
  return request(`${organizationUrl(organizationId)}/owner-invitations/${encodeURIComponent(invitationId)}/resend`, authToken, { method: "POST" });
}

export function revokeOwnerInvitation(
  authToken: string,
  organizationId: number,
  invitationId: string,
): Promise<OrganizationInvitation> {
  return request(`${organizationUrl(organizationId)}/owner-invitations/${encodeURIComponent(invitationId)}/revoke`, authToken, { method: "POST" });
}

export function inviteOrganizationEmployee(
  authToken: string,
  organizationId: number,
  payload: InviteOrganizationEmployeeRequest,
): Promise<SuperAdminOrganizationEmployee> {
  return request(`${organizationUrl(organizationId)}/employees`, authToken, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function setOrganizationEmployeeActive(
  authToken: string,
  organizationId: number,
  employee: SuperAdminOrganizationEmployee,
  active: boolean,
): Promise<SuperAdminOrganizationEmployee> {
  return request(`${organizationUrl(organizationId)}/employees/${encodeURIComponent(employee.id)}/status`, authToken, {
    method: "PATCH",
    body: JSON.stringify({
      isActive: active,
      expectedIsActive: !employee.deactivated,
    }),
  });
}
